import { Component, OnInit } from '@angular/core';
import { LeadService } from '../../../../../../shared/store/lead/lead.service';
import { distinctUntilChanged, filter, Observable } from 'rxjs';
import { CarInsuranceLeadModel } from '../../../../../../shared/store/lead/types';
import {
  ControlContainer,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { formatDate } from '../../../../../../shared/helper/datehelper';
import { Entity } from '../../../../../../shared/store/view/types';
import { take } from 'rxjs/operators';
import {
  DataType,
  DropdownStyle,
} from '../../../../../../shared/store/typeahead/types';
import {
  removeValidators,
  setValidators,
} from '../../../../../../shared/helper/formhelper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MAX_DATE_MASK_SAFE_GUARD } from '../../../../../../shared/constants/date.constants';

@UntilDestroy()
@Component({
  selector: 'app-lead-form-vehicle',
  templateUrl: './lead-form-vehicle.component.html',
  styleUrls: ['./lead-form-vehicle.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class LeadFormVehicleComponent implements OnInit {
  carDetail$: Observable<CarInsuranceLeadModel>;

  leadForm: FormGroup;

  protected readonly Entity = Entity;
  protected readonly DataType = DataType;
  protected readonly DropdownStyle = DropdownStyle;

  isRegistrationHolderNameVisible = false;
  isRegistrationHolderCompanyVisible = false;

  maxDateMaskSafeGuard: string = MAX_DATE_MASK_SAFE_GUARD;

  constructor(
    private readonly leadService: LeadService,
    private parent: FormGroupDirective,
    private formBuilder: FormBuilder,
  ) {
    this.carDetail$ = this.leadService.getCarDetail.value$;
  }

  ngOnInit(): void {
    this.leadForm = this.parent.form;
    this.leadForm.addControl(
      'vehicleFormGroup',
      this.formBuilder.group({
        vehicleRegistrationHolder: [''],
        vehicleRegistrationHolderCompanyName: [''],
        vehicleRegistrationHolderNameLastName: [''],
        vehicleRegistrationHolderNameFirstName: [''],
        vehicleRegistrationHolderBirthdate: [undefined],
        vehicleIsDriverVehiculeRegistrationHolder: [true],
        vehicleLicensePlateNumber: [''],
        vehicleBrand: [undefined, [Validators.required]],
        vehicleModel: [undefined, [Validators.required]],
        vehicleExactModel: [undefined],
        vehicleCarBody: [undefined, [Validators.required]],
        vehicleCarEnergy: [undefined, [Validators.required]],
        vehicleCarPower: [''],
        vehicleUsage: [undefined],
        vehicleUsageProSupp: [undefined],
        vehicleParkingType: [undefined],
        vehicleDateOfCirculation: [undefined, [Validators.required]],
      }),
    );

    this.carDetail$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((x: CarInsuranceLeadModel) => {
        this.leadForm.patchValue({
          vehicleFormGroup: {
            vehicleRegistrationHolder: x.vehiculeRegistrationHolder,
            vehicleRegistrationHolderCompanyName:
              x.vehiculeRegistrationHolderCompanyName,
            vehicleRegistrationHolderNameLastName:
              x.vehiculeRegistrationHolderNameLastName,
            vehicleRegistrationHolderNameFirstName:
              x.vehiculeRegistrationHolderNameFirstName,
            vehicleRegistrationHolderBirthdate: formatDate(
              x.vehiculeRegistrationHolderBirthdate,
              'ToInverseSqlDate',
            ),
            vehicleLicensePlateNumber: x.licensePlateNumber,
            vehicleBrand: x.brand,
            vehicleModel: x.model,
            vehicleExactModel: x.exactModel,
            vehicleCarBody: x.carBody,
            vehicleCarEnergy: x.carEnergie,
            vehicleCarPower: x.carPower,
            vehicleUsage: x.vehiculeUsage,
            vehicleUsageProSupp: x.vehiculeUsageProSupp,
            vehicleParkingType: x.parkingType,
            vehicleDateOfCirculation: formatDate(
              x.dateOfCirculation,
              'ToInverseSqlDate',
            ),
          },
        });
      });

    this.leadForm.controls['vehicleFormGroup']
      .get('vehicleUsage')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((x) => {
        switch (x) {
          case 'privepro':
            setValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleUsageProSupp',
              ),
              Validators.required,
            );
            break;
          default:
            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleUsageProSupp',
              ),
              Validators.required,
            );
            break;
        }
      });

    this.leadForm.controls['vehicleFormGroup']
      .get('vehicleRegistrationHolder')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((x) => {
        switch (x) {
          case 'Société':
          case 'Leasing':
            setValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderCompanyName',
              ),
              Validators.required,
            );
            this.isRegistrationHolderCompanyVisible = true;

            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderNameLastName',
              ),
              Validators.required,
            );
            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderNameFirstName',
              ),
              Validators.required,
            );
            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderBirthdate',
              ),
              Validators.required,
            );
            this.isRegistrationHolderNameVisible = false;

            break;
          case 'Conjoint':
          case 'Concubin':
          case 'Père ou mère':
          case 'Enfant':
          case 'Autre':
            setValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderNameLastName',
              ),
              Validators.required,
            );
            setValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderNameFirstName',
              ),
              Validators.required,
            );
            setValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderBirthdate',
              ),
              Validators.required,
            );
            this.isRegistrationHolderNameVisible = true;

            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderCompanyName',
              ),
              Validators.required,
            );
            this.isRegistrationHolderCompanyVisible = false;
            break;
          default:
            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderNameLastName',
              ),
              Validators.required,
            );
            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderNameFirstName',
              ),
              Validators.required,
            );
            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderBirthdate',
              ),
              Validators.required,
            );
            this.isRegistrationHolderNameVisible = false;

            removeValidators(
              this.leadForm.controls['vehicleFormGroup'].get(
                'vehicleRegistrationHolderCompanyName',
              ),
              Validators.required,
            );
            this.isRegistrationHolderCompanyVisible = false;
            break;
        }
      });

    this.leadForm.controls['vehicleFormGroup']
      .get('vehicleBrand')
      ?.valueChanges?.pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((result) => {
        this.leadForm.patchValue({
          vehicleFormGroup: {
            vehicleModel: undefined,
            vehicleExactModel: undefined,
          },
        });
      });
    this.leadForm.controls['vehicleFormGroup']
      .get('vehicleModel')
      ?.valueChanges?.pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((result) => {
        this.leadForm.patchValue({
          vehicleFormGroup: {
            vehicleExactModel: undefined,
          },
        });
      });
  }

  onChangeSetVehicleRegistrationValidators(checked: boolean) {
    if (checked) {
      removeValidators(
        this.leadForm.controls['vehicleFormGroup'].get(
          'vehicleRegistrationHolder',
        ),
        Validators.required,
      );
      removeValidators(
        this.leadForm.controls['vehicleFormGroup'].get(
          'vehicleRegistrationHolderNameLastName',
        ),
        Validators.required,
      );
      removeValidators(
        this.leadForm.controls['vehicleFormGroup'].get(
          'vehicleRegistrationHolderNameFirstName',
        ),
        Validators.required,
      );
      removeValidators(
        this.leadForm.controls['vehicleFormGroup'].get(
          'vehicleRegistrationHolderBirthdate',
        ),
        Validators.required,
      );
      removeValidators(
        this.leadForm.controls['vehicleFormGroup'].get(
          'vehicleRegistrationHolderCompanyName',
        ),
        Validators.required,
      );
      return;
    }

    setValidators(
      this.leadForm.controls['vehicleFormGroup'].get(
        'vehicleRegistrationHolder',
      ),
      Validators.required,
    );
  }
}
